//@ts-check
import produce from 'immer';
import ACTIONS from './actions';
import formLoader from './formLoader';
import formPagesLogic from './formPagesLogic';
import validationLogic from '../validation';
import deepMerge from '../../../../utils/deepMerge';
import layout from '../layout';
import fieldsValuesCalculator from './data/fieldsValuesCalculator';
import formVisibilityLogic from './formVisibilityLogic';
import formHelper from '../utils/formHelper';
import fieldsHelper from '../utils/fieldsHelper';
import visibility from '../visibility';
/**
 * @param {import('./types').EcrionFormState} state
 * @param {any} action
 */

var formDataReducer = function formDataReducer(state, action) {
  if (!action) return state;

  switch (action.type) {
    case 'SET_STATE':
      return action.payload;

    case ACTIONS.FORM_LOADED:
      {
        var form = action.payload.form;
        return formLoader(form);
      }

    case ACTIONS.SET_ACTIVE_PAGE:
      return produce(state, function (draft) {
        return formPagesLogic.changePage(draft, action.payload);
      });

    case ACTIONS.FORM_DATA_CHANGED:
      return produce(state, function (draft) {
        // recalculate the layout since there are some changes in fields
        draft.layout = layout.calculate(draft);
        draft.layout.pages.forEach(function (pageId) {
          if (!draft.meta[pageId] || !draft.meta[pageId].error) return;
          var pageLayout = state.layout.containers[pageId];
          if (!pageLayout) return;
          var fields = validationLogic.helper.findVisibleFields(pageLayout, draft);
          var pageError = '';
          fields.forEach(function (fieldId) {
            if (pageError) return; // nothing to do anymore

            if (!draft.meta[fieldId] || !draft.meta[fieldId].error) return;
            pageError = draft.meta[fieldId].error;
          });
          if (!pageError) formHelper.setError(draft, pageId, '');
        });
      });

    case 'DATA_BINDING_RESOLVED':
      return produce(state, function (draft) {
        draft.stage = 'loaded';
        draft.hasVisibleInputFields = formVisibilityLogic.hasVisibleInputFields(draft); // form is now loaded and pristine like in the day 'she' was born

        draft.meta['FORM'] = draft.meta['FORM'] || {};
        draft.meta['FORM'].dirty = false;
      });

    case 'FORM_DATA_LOADED':
      return produce(state, function (draft) {
        draft.stage = 'dataLoaded';
      });

    case 'FORM_CONTEXT_LOADED':
      return produce(state, function (draft) {
        var _action$payload = action.payload,
            subscriptions = _action$payload.subscriptions,
            environmentVars = _action$payload.environmentVars,
            permissions = _action$payload.permissions;
        draft.context.subscriptions = subscriptions;
        draft.context.permissions = permissions;
        draft.context.environmentVars = deepMerge(draft.context.environmentVars, environmentVars); // move to this new stage

        draft.stage = 'contextLoaded';
      });

    case 'SET_ACTIVE_FIELD':
      return produce(state, function (draft) {
        var fieldId = action.payload;
        draft.meta[fieldId] = draft.meta[fieldId] || {}; // remove old active field

        Object.keys(draft.meta).forEach(function (key) {
          if (draft.meta[key].active) draft.meta[key].active = false;
        });
        draft.meta[fieldId].active = true;
      });

    case ACTIONS.SET_FIELD_VALUE:
      {
        var _action$payload2 = action.payload,
            fieldId = _action$payload2.fieldId,
            value = _action$payload2.value;
        return produce(state, function (draft) {
          return fieldsValuesCalculator.changeFieldValue(draft, fieldId, value, true);
        });
      }

    case ACTIONS.SET_REPEATING_FIELD_VALUE:
      {
        var _action$payload3 = action.payload,
            _fieldId = _action$payload3.fieldId,
            _value = _action$payload3.value;
        return produce(state, function (draft) {
          return fieldsValuesCalculator.changeRepeatingFieldValue(draft, _fieldId, _value);
        });
      }

    case ACTIONS.SET_FIELDS_VALUES:
      return produce(state, function (draft) {
        if (!action.payload) return; // first we change fields values

        Object.keys(action.payload).forEach(function (fieldId) {
          fieldsValuesCalculator.changeFieldValue(draft, fieldId, action.payload[fieldId]);
        }); // calculate visibility for all fields based on modified fields

        var ids = Object.keys(action.payload);
        var fields = fieldsHelper.collectFieldsForVisbilityChanged(state, ids);
        formVisibilityLogic.calculate(fields, draft); // recalculate the layout

        draft.layout = layout.calculate(draft);
        draft.layout.pages.forEach(function (pageId) {
          if (!draft.meta[pageId] || !draft.meta[pageId].error) return;
          var pageLayout = state.layout.containers[pageId];
          if (!pageLayout) return;
          var fields = validationLogic.helper.findVisibleFields(pageLayout, draft);
          var pageError = '';
          fields.forEach(function (fieldId) {
            if (pageError) return; // nothing to do anymore

            if (!draft.meta[fieldId] || !draft.meta[fieldId].error) return;
            pageError = draft.meta[fieldId].error;
          });
          if (!pageError) formHelper.setError(draft, pageId, '');
        });
      });

    case ACTIONS.UPDATE_VISIBILITY:
      return produce(state, function (draft) {
        var fields = fieldsHelper.collectFieldsForVisbilityChanged(draft, action.payload);
        formVisibilityLogic.calculate(fields, draft);
        fields.forEach(function (field) {
          var isVisible = visibility.calculate(field.id, draft);
          formHelper.setMetaProp(draft, field.id, 'hidden', !isVisible);
        });
      });

    case ACTIONS.SET_FIELDS_PROPERTIES:
      return produce(state, function (draft) {
        if (!action.payload) return;
        action.payload.forEach(function (option) {
          if (!draft.fields[option.id]) return; // no field with this id (defensive check)

          Object.keys(option.updateProperties).forEach(function (property) {
            if (option.updateProperties[property] === undefined) {
              delete draft.fields[property];
            } else {
              draft.fields[option.id][property] = option.updateProperties[property];
            }
          });
        });
      });

    case ACTIONS.ADD_FIELDS:
      return produce(state, function (draft) {
        if (!action.payload) return;
        Object.keys(action.payload).forEach(function (fieldId) {
          draft.fields[fieldId] = action.payload[fieldId]; // no action needed if the field is visible

          if (visibility.calculate(fieldId, draft)) return; // hide the field

          draft.meta[fieldId] = draft.meta[fieldId] || {};
          draft.meta[fieldId].hidden = true;
        });
      });

    case ACTIONS.SET_FIELD_TOUCHED:
      return produce(state, function (draft) {
        formHelper.setMetaProp(draft, action.payload.fieldId, 'touched', true); // remove old active field

        Object.keys(draft.meta).forEach(function (key) {
          if (draft.meta[key].active) draft.meta[key].active = false;
        });
        draft.meta['FOCUSED_FIELD'] = action.payload.fieldId;
      });

    case ACTIONS.SET_FIELD_BLURED:
      return produce(state, function (draft) {
        var fieldId = action.payload.fieldId; // remove old active field

        Object.keys(draft.meta).forEach(function (key) {
          if (draft.meta[key].active) draft.meta[key].active = false;
        });
        formHelper.setError(draft, fieldId, validationLogic.validateField(fieldId, state));
      });

    case ACTIONS.SET_FIELD_META:
      return produce(state, function (draft) {
        var _action$payload4 = action.payload,
            fieldId = _action$payload4.fieldId,
            name = _action$payload4.name,
            value = _action$payload4.value;
        formHelper.setMetaProp(draft, fieldId, name, value);
      });

    case ACTIONS.SET_FIELDS_META:
      return produce(state, function (draft) {
        draft.meta = deepMerge(state.meta, action.payload);
      });

    case ACTIONS.SET_FIELD_ERROR:
      return produce(state, function (draft) {
        var _action$payload5 = action.payload,
            fieldId = _action$payload5.fieldId,
            error = _action$payload5.error;
        formHelper.setError(draft, fieldId, error);
      });

    case ACTIONS.SET_FORM_DIRTY:
      return produce(state, function (draft) {
        draft.meta['FORM'] = draft.meta['FORM'] || {};
        draft.meta['FORM'].dirty = action.payload;
      });

    case ACTIONS.SET_FORM_OPTIONS:
      if (!action.payload) return state;
      return produce(state, function (draft) {
        draft.options = action.payload;
      });

    case ACTIONS.SET_FORM_RENDER_OPTIONS:
      {
        if (!action.payload) return state;
        return produce(state, function (draft) {
          draft.renderOptions = deepMerge(draft.renderOptions, action.payload);
        });
      }

    default:
      break;
  }

  return state;
};

export default formDataReducer;